import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { planHasFeature } from "@/lib/platform-features";

interface PlanLimits {
  planName: string;
  maxOrdersPerMonth: number | null;
  maxProducts: number | null;
  maxCategories: number | null;
  currentProducts: number;
  currentCategories: number;
  currentMonthOrders: number;
  canAddProduct: boolean;
  canAddCategory: boolean;
  canAcceptOrder: boolean;
  features: Record<string, boolean>;
  hasFeature: (key: string) => boolean;
  loading: boolean;
}

export const usePlanLimits = (restaurantId: string | null): PlanLimits => {
  const [limits, setLimits] = useState<PlanLimits>({
    planName: "Sem plano",
    maxOrdersPerMonth: null,
    maxProducts: null,
    maxCategories: null,
    currentProducts: 0,
    currentCategories: 0,
    currentMonthOrders: 0,
    canAddProduct: true,
    canAddCategory: true,
    canAcceptOrder: true,
    features: {},
    hasFeature: () => true,
    loading: true,
  });

  useEffect(() => {
    if (!restaurantId) return;

    const load = async () => {
      const { data: rest } = await supabase
        .from("restaurants")
        .select("plan_id")
        .eq("id", restaurantId)
        .single();

      let plan: any = null;
      if (rest?.plan_id) {
        const { data } = await supabase
          .from("plans")
          .select("*")
          .eq("id", rest.plan_id)
          .single();
        plan = data;
      }

      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const [productsRes, categoriesRes, ordersRes] = await Promise.all([
        supabase.from("products").select("id", { count: "exact", head: true }).eq("restaurant_id", restaurantId),
        supabase.from("categories").select("id", { count: "exact", head: true }).eq("restaurant_id", restaurantId),
        supabase.from("orders").select("id", { count: "exact", head: true }).eq("restaurant_id", restaurantId).gte("created_at", firstDay),
      ]);

      const currentProducts = productsRes.count || 0;
      const currentCategories = categoriesRes.count || 0;
      const currentMonthOrders = ordersRes.count || 0;

      const maxProducts = plan?.max_products ?? null;
      const maxCategories = plan?.max_categories ?? null;
      const maxOrders = plan?.max_orders_per_month ?? null;

      // Parse features from plan
      const planFeatures = plan?.features || {};
      const featuresObj: Record<string, boolean> = typeof planFeatures === "object" && !Array.isArray(planFeatures)
        ? planFeatures
        : {};

      const hasFeatureFn = (key: string): boolean => {
        // If no plan at all, block all gated features
        if (!plan) return false;
        // If plan has no features configured, allow everything (graceful default for plans without feature config)
        if (!plan.features || Object.keys(featuresObj).length === 0) return true;
        return planHasFeature(plan.features, key);
      };

      setLimits({
        planName: plan?.name || "Sem plano",
        maxOrdersPerMonth: maxOrders,
        maxProducts,
        maxCategories,
        currentProducts,
        currentCategories,
        currentMonthOrders,
        canAddProduct: maxProducts === null || currentProducts < maxProducts,
        canAddCategory: maxCategories === null || currentCategories < maxCategories,
        canAcceptOrder: maxOrders === null || currentMonthOrders < maxOrders,
        features: featuresObj,
        hasFeature: hasFeatureFn,
        loading: false,
      });
    };

    load();
  }, [restaurantId]);

  return limits;
};
